import { useContext, useEffect, useState, useCallback } from "react";
import { UserContext } from "../../../context/User.context";
import {
  Button,
  Table,
  Input,
  notification,
  Pagination,
  Tag,
  Tooltip,
} from "antd";
import { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { useHttp } from "../../../utils/DataHendler";
import { useMediaQuery } from "react-responsive";

export const LocationTable = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const { getData } = useHttp();
  const { user } = useContext(UserContext);
  const { Search } = Input;

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [api, contextHolder] = notification.useNotification();

  useEffect(() => {
    fetchData({ page: 1, limit: 10 });
    // eslint-disable-next-line
  }, [user]);

  // Fetch location data sesuai struktur baru
  const fetchData = async (queryParams = {}) => {
    setLoading(true);
    try {
      const response = await getData(
        `/location`,
        { params: { ...queryParams } },
        true
      );
      if (response.success) {
        const locationData = response.data?.data || [];
        const paginationData = response.data?.pagination || {};
        setData(
          locationData.map((item) => ({
            key: item.id,
            id: item.id,
            code: item.code,
            name: item.name,
            latitude: item.latitude,
            longitude: item.longitude,
            start_time: item.start_time,
            end_time: item.end_time,
            description: item.description,
            active: item.active,
            updated_at: item.updated_at,
          }))
        );
        setPagination({
          page: paginationData.page || 1,
          limit: paginationData.limit || 10,
          total: paginationData.total || 0,
          totalPages: paginationData.totalPages || 1,
        });
      }
    } catch (error) {
      api.error({
        message: "Failed to fetch location data",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler untuk perubahan halaman
  const handlePageChange = (page, pageSize) => {
    fetchData({ ...filters, page, limit: pageSize });
    setPagination((prev) => ({ ...prev, page, limit: pageSize }));
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value) => {
      const newFilters = { ...filters, search: value || undefined, page: 1 };
      setFilters(newFilters);
      fetchData(newFilters);
    }, 600),
    [filters]
  );

  // Table columns sesuai data baru
  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      width: 110,
      ellipsis: true,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      ellipsis: true,
    },
    {
      title: "Latitude",
      dataIndex: "latitude",
      key: "latitude",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Longitude",
      dataIndex: "longitude",
      key: "longitude",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Start Time",
      dataIndex: "start_time",
      key: "start_time",
      width: 100,
      ellipsis: true,
    },
    {
      title: "End Time",
      dataIndex: "end_time",
      key: "end_time",
      width: 100,
      ellipsis: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
      ellipsis: true,
      render: (val) =>
        val ? (
          <Tooltip title={val}>
            <span>{val}</span>
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Active",
      dataIndex: "active",
      key: "active",
      width: 80,
      render: (val) =>
        val ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag>,
    },
    {
      title: "Updated At",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 150,
      render: (val) =>
        val
          ? new Date(val).toLocaleString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })
          : "-",
      ellipsis: true,
    },
    {
      title: "Action",
      key: "action",
      width: 90,
      fixed: "right",
      render: (_, record) => (
        <Button
          icon={<EyeOutlined />}
          onClick={() => navigate(`/user/location/detail/${record.code}`)}
        />
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <div
        style={{
          marginBottom: "15px",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: isMobile ? "flex-start" : "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          gap: isMobile ? "10px" : "0px",
        }}
      >
        <Search
          style={{
            marginRight: isMobile ? "0px" : "5px",
            width: isMobile ? "100%" : "200px",
          }}
          placeholder="Search code or name"
          allowClear
          onChange={(e) => debouncedSearch(e.target.value.trim())}
          enterButton
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate("/user/location-add")}
          style={{ marginTop: isMobile ? 10 : 0 }}
        >
          Add Location
        </Button>
      </div>
      <Table
        dataSource={data}
        columns={columns}
        loading={loading}
        scroll={{ x: 1300, y: 550 }}
        pagination={false}
        size="small"
        style={{ width: "100%" }}
        bordered
      />
      <Pagination
        current={pagination.page}
        pageSize={pagination.limit}
        total={pagination.total}
        onChange={handlePageChange}
        showSizeChanger={false}
        style={{ marginTop: 20, textAlign: "center" }}
      />
    </>
  );
};
